"use client";

import { io, type Socket } from "socket.io-client";

type Handler = (data: any) => void;

function joinEventFor(
  channel: string,
): { event: string; payload?: string } | null {
  if (channel === "admin-feedbacks") return { event: "join-admin-feedbacks" };
  if (channel === "admin-reviews") return { event: "join-admin-reviews" };
  const userMatch = channel.match(/^user-(.+)$/);
  if (userMatch) return { event: "join-user-room", payload: userMatch[1] };
  const feedbackMatch = channel.match(/^feedback-(.+)$/);
  if (feedbackMatch)
    return { event: "join-feedback-room", payload: feedbackMatch[1] };
  return null;
}

/**
 * Mirrors the tiny slice of pusher-js's `Channel` API this codebase actually
 * uses. Handlers registered via `bind()` are queued if the underlying socket
 * isn't connected yet (`attach()` hasn't run), then wired up once it is —
 * `bind()` can be called synchronously right after `subscribe()` regardless
 * of connection timing, same as pusher-js.
 */
class SocketIOChannel {
  private socket: Socket | null = null;
  private handlers = new Map<string, Set<Handler>>();

  attach(socket: Socket) {
    this.socket = socket;
    this.handlers.forEach((set, event) => {
      set.forEach((cb) => socket.on(event, cb));
    });
  }

  bind(event: string, callback: Handler) {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(callback);
    if (this.socket) this.socket.on(event, callback);
  }

  unbind(event: string, callback?: Handler) {
    const set = this.handlers.get(event);
    if (!set) return;
    if (callback) {
      set.delete(callback);
      this.socket?.off(event, callback);
    } else {
      const socket = this.socket;
      if (socket) set.forEach((cb) => socket.off(event, cb));
      set.clear();
    }
  }

  unbind_all() {
    const socket = this.socket;
    if (socket) {
      this.handlers.forEach((set, event) => {
        set.forEach((cb) => socket.off(event, cb));
      });
    }
    this.handlers.clear();
  }
}

const TOKEN_REFRESH_INTERVAL_MS = 4 * 60 * 1000; // token is ~5 min, refresh at 4

/**
 * Drop-in replacement for `pusherClient` — same `subscribe(channel).bind(...)`
 * / `unsubscribe(channel)` shape, backed by a single shared Socket.IO
 * connection to busyexpand-socket-server instead of Pusher. Every existing
 * call site only needs its import swapped, nothing else.
 */
class SocketIORealtimeClient {
  private socket: Socket | null = null;
  private connecting: Promise<void> | null = null;
  private channels = new Map<string, SocketIOChannel>();
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  private async fetchToken(): Promise<string | null> {
    try {
      const res = await fetch("/api/socket-token");
      if (!res.ok) return null;
      const data = await res.json();
      return data?.token ?? null;
    } catch {
      return null;
    }
  }

  private joinRoom(channel: string) {
    if (!this.socket) return;
    const join = joinEventFor(channel);
    if (!join) return;
    this.socket.emit(join.event, join.payload);
  }

  private async connect() {
    const url = process.env.NEXT_PUBLIC_SOCKET_SERVER_URL;
    if (!url) {
      console.error(
        "NEXT_PUBLIC_SOCKET_SERVER_URL is not set — cannot connect to the socket server.",
      );
      return;
    }
    const token = await this.fetchToken();
    if (!token) return; // no active session — nothing to authenticate with

    this.socket = io(url, { auth: { token } });

    this.channels.forEach((channel, name) => {
      channel.attach(this.socket as Socket);
      this.joinRoom(name);
    });

    this.socket.on("connect", () => {
      this.channels.forEach((_channel, name) => this.joinRoom(name));
    });

    this.socket.on("connect_error", async () => {
      // Most likely an expired/invalid token — fetch a fresh one so the
      // library's built-in reconnection logic succeeds on its next attempt.
      const fresh = await this.fetchToken();
      if (fresh && this.socket) this.socket.auth = { token: fresh };
    });

    this.refreshTimer = setInterval(async () => {
      const fresh = await this.fetchToken();
      if (fresh && this.socket) this.socket.auth = { token: fresh };
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  private ensureConnected(): Promise<void> {
    if (this.socket) return Promise.resolve();
    if (!this.connecting) this.connecting = this.connect();
    return this.connecting;
  }

  subscribe(channel: string): SocketIOChannel {
    let ch = this.channels.get(channel);
    if (!ch) {
      ch = new SocketIOChannel();
      this.channels.set(channel, ch);
    }
    this.ensureConnected().then(() => {
      if (!this.socket) return;
      ch!.attach(this.socket);
      this.joinRoom(channel);
    });
    return ch;
  }

  unsubscribe(channel: string) {
    const ch = this.channels.get(channel);
    if (ch) {
      ch.unbind_all();
      this.channels.delete(channel);
    }
    this.socket?.emit("leave-room", channel);
  }
}

export const socketIOClient = new SocketIORealtimeClient();
